/**
 * usePatientFlexibleActivities Hook
 *
 * Fetches a patient's active care plan and derives available flexible activities
 * for adding plan-backed tasks. Used by FollowUpTaskSection and Task.form.
 * Phase 2 of standalone task deprecation.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { CarePlan } from "@medplum/fhirtypes";
import {
  CARE_PLAN_CATEGORY_SYSTEM,
  getFlexibleActivitiesForType,
  type CarePlanCategoryCode,
} from "@benchmark/core/constants";
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

const ACTIVE_CARE_PLAN_COUNT = 10;

export type FlexibleActivitySelectOption = {
  value: string;
  label: string;
  description: string;
};

export type UsePatientFlexibleActivitiesResult = {
  carePlan: CarePlan | null;
  carePlanType: CarePlanCategoryCode | null;
  flexibleActivityOptions: FlexibleActivitySelectOption[];
  isLoading: boolean;
  hasActiveCarePlan: boolean;
};

/**
 * Maps flexible care plan activities to UI select options.
 * @agentic connections
 */
export const buildFlexibleActivitySelectOptions = (
  carePlanType: CarePlanCategoryCode,
): FlexibleActivitySelectOption[] =>
  getFlexibleActivitiesForType(carePlanType).map((activity) => ({
    value: activity.code,
    label: activity.display,
    description: activity.description,
  }));

export const usePatientFlexibleActivities = (
  patientId: string | undefined,
): UsePatientFlexibleActivitiesResult => {
  const medplum = useMedplum();
  const [carePlans, setCarePlans] = useState<CarePlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCarePlans = useCallback(async () => {
    if (!patientId) {
      setCarePlans([]);
      return;
    }

    setIsLoading(true);
    try {
      const bundle = await medplum.search("CarePlan", {
        subject: `Patient/${patientId}`,
        status: "active",
        _count: ACTIVE_CARE_PLAN_COUNT.toString(),
      });

      const list: CarePlan[] = (bundle.entry ?? []).flatMap((e) =>
        e.resource?.resourceType === "CarePlan" ? [e.resource as CarePlan] : [],
      );
      setCarePlans(list);
    } catch (err) {
      console.error("Error fetching care plans for flexible activities:", err);
      setCarePlans([]);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, medplum]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchCarePlans();
    });
  }, [fetchCarePlans]);

  // Auto-refetch on care-plan events for this patient.
  useEventDrivenRefetch(
    [
      DomainEventNames.CarePlanCreated,
      DomainEventNames.CarePlanActivated,
      DomainEventNames.CarePlanUpdated,
      DomainEventNames.CarePlanCompleted,
      DomainEventNames.CarePlanCancelled,
    ],
    {
      refetch: fetchCarePlans,
      enabled: !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
    },
  );

  // Use first active care plan (prefer CoCM > triage > intake)
  const activePlan = useMemo(() => {
    const priorityOrder: CarePlanCategoryCode[] = ["cocm", "triage", "intake"];
    for (const type of priorityOrder) {
      const match = carePlans.find((cp) =>
        cp.category?.some((cat) => cat.coding?.some((c) => c.code === type)),
      );
      if (match) return match;
    }
    return carePlans[0] ?? null;
  }, [carePlans]);

  // Derive care plan type from category coding
  const carePlanType = useMemo((): CarePlanCategoryCode | null => {
    if (!activePlan) return null;
    const code = activePlan.category?.[0]?.coding?.find(
      (c) => c.system === CARE_PLAN_CATEGORY_SYSTEM,
    )?.code;
    return (code as CarePlanCategoryCode) ?? null;
  }, [activePlan]);

  // Get flexible activities for that care plan type
  const flexibleActivityOptions =
    useMemo((): FlexibleActivitySelectOption[] => {
      if (!carePlanType) return [];
      return buildFlexibleActivitySelectOptions(carePlanType);
    }, [carePlanType]);

  return {
    carePlan: activePlan,
    carePlanType,
    flexibleActivityOptions,
    isLoading,
    hasActiveCarePlan: activePlan !== null,
  };
};
